import clsx from "clsx"
import {useState} from "react"
import {SubmitHandler, useForm} from "react-hook-form"
import Button from "~/components/UI/Button/Button"
import CheckBox from "~/components/UI/CheckBox/CheckBox"
import FileUpload from "~/components/UI/FileUpload/FileUpload"
import InputText from "~/components/UI/InputText/InputText"
import Radio from "~/components/UI/Radio/Radio"
import RadioGroup from "~/components/UI/Radio/RadioGroup"
import TextArea from "~/components/UI/TextArea/TextArea"
import CheckIcon from "~/icons/CheckIcon"
import {useLabelsQuery} from "~/queries/LabelsQuery"
import ErrorIcon from "../../icons/ErrorIcon"
import {useAuthenticationLabelsQuery} from "../../queries/AuthenticationLabelsQuery"
import {emailRegex, nameRegex, phoneRegex, textRegex} from "../../utilities/RegexStrings"
import "./Form.css"

interface ContactUsFullFormProps extends React.FormHTMLAttributes<HTMLFormElement> {}

// Se il nome dei campi è caotico, dipende dalla form.
type ContactUsFullFields = {
    Name_First: string
    Name_Last: string
    Email: string
    SingleLine: string //Company name
    PhoneNumber_countrycode: string
    Dropdown: string
    Dropdown1: string //Tipo
    FileUpload: File
    MultiLine: string
    DecisionBox: "on" | ""
    DecisionBox1: "on" | ""
    DecisionBox2: "on" | ""
}

type SubmitStatus = undefined | "success" | "error"

const ContactUsFullForm: React.FC<ContactUsFullFormProps> = props => {
    const {className, children, ...otherProps} = props
    const [submitStatus, setSubmitStatus] = useState<SubmitStatus>()

    const {
        register,
        handleSubmit,
        watch,
        formState: {errors},
    } = useForm<ContactUsFullFields>()
    const labels = {...useLabelsQuery(), ...useAuthenticationLabelsQuery()}

    const onFormSubmitHandler: SubmitHandler<ContactUsFullFields> = async inputData => {
        const data = {...inputData, PhoneNumber_countrycode: inputData.PhoneNumber_countrycode.replace("+", "00")}

        const url =
            "https://forms.zohopublic.eu/adkaora/form/AdKaoranewwebsiteLAVORACONNOI/formperma/0UrFrFIFXXh4TlYIYqqlCXQMMRyLHuAazd6AyGFit14/htmlRecords/submit"

        const formData = new FormData()

        for (const field in data) {
            type ObjectKey = keyof typeof data
            const fieldKey = field as ObjectKey
            if (data[fieldKey] instanceof FileList) {
                const fl = data[fieldKey] as unknown as FileList
                if (fl[0]) {
                    formData.append(field, fl[0], fl[0].name)
                }
                continue
            }
            if (typeof data[fieldKey] === "boolean") {
                const bool = data[fieldKey] as unknown as boolean
                if (bool) {
                    formData.append(field, "on")
                } else {
                    formData.append(field, "")
                }
                continue
            }

            formData.append(field, data[fieldKey])
        }

        const requestOptions: RequestInit = {
            method: "POST",
            mode: "cors",
            body: formData,
            redirect: "manual",
            cache: "no-cache",
            credentials: "same-origin",
        }

        await fetch(url, requestOptions)
            .then(result => {})
            .catch((error: Error) => {})
        setSubmitStatus("success")
    }

    return (
        <form
            className={clsx("form-ykf9", "white-background-ykf9", className)}
            onSubmit={handleSubmit(onFormSubmitHandler)}
            {...otherProps}
        >
            <InputText
                type="text"
                placeholder={labels.name}
                invalid={errors.Name_First !== undefined}
                {...register("Name_First", {required: true, pattern: nameRegex})}
            />
            <InputText
                type="text"
                placeholder={labels.lastName}
                invalid={errors.Name_Last !== undefined}
                {...register("Name_Last", {required: true, pattern: nameRegex})}
            />
            <InputText
                type="text"
                placeholder={labels.company}
                invalid={errors.SingleLine !== undefined}
                {...register("SingleLine", {required: false, pattern: nameRegex})}
            />
            <InputText
                type="email"
                placeholder={labels.eMail}
                invalid={errors.Email !== undefined}
                {...register("Email", {
                    required: true,
                    pattern: emailRegex,
                })}
            />
            <div className="full-width-ykf9">
                <InputText
                    containerClassName="full-width-ykf9 phone-input-ykf9"
                    type="phone"
                    placeholder={labels.phoneNumber}
                    invalid={errors.PhoneNumber_countrycode !== undefined}
                    {...register("PhoneNumber_countrycode", {
                        required: true,
                        pattern: phoneRegex,
                    })}
                />
                {errors.PhoneNumber_countrycode !== undefined && (
                    <div className="messages-box-pt86">{labels.validationInvalidPhoneNumber}</div>
                )}
            </div>
            <FileUpload
                id="file"
                placeholder={labels.attachedFile}
                accept=".pdf,.zip,.rar,.7z"
                invalid={errors.FileUpload !== undefined}
                {...register("FileUpload", {required: false})}
            />
            <RadioGroup
                className="span2-ykf9"
                label={labels.type ? labels.type : ""}
                invalid={errors.Dropdown1 !== undefined}
                name="Dropdown1"
            >
                <Radio label={labels.advertiser} value={`advertiser`} {...register("Dropdown1", {required: false})} />
                <Radio label={labels.publisher} value={`publisher`} {...register("Dropdown1", {required: false})} />
                <Radio label={labels.candidate} value={`cadidate`} {...register("Dropdown1", {required: false})} />
                <Radio label={labels.other} value={`other`} {...register("Dropdown1", {required: false})} />
            </RadioGroup>
            <TextArea
                placeholder={labels.message}
                rows={8}
                className="span2-ykf9"
                invalid={errors.MultiLine !== undefined}
                {...register("MultiLine", {required: false, pattern: textRegex})}
            />
            <CheckBox
                label={labels.privacyAcceptance}
                className="span2-ykf9"
                invalid={errors.DecisionBox !== undefined}
                {...register("DecisionBox", {required: true})}
            />
            <CheckBox
                label={labels.userProfiling}
                className="span2-ykf9"
                invalid={errors.DecisionBox1 !== undefined}
                {...register("DecisionBox1", {required: false})}
            />
            <CheckBox
                label={labels.userMarketing}
                className="span2-ykf9"
                invalid={errors.DecisionBox2 !== undefined}
                {...register("DecisionBox2", {required: false})}
            />
            <Button className="span2-ykf9 button-ykf9" type="submit">
                {labels.submitForm}
            </Button>
            {submitStatus === "success" && (
                <div className="overlay-ykf9">
                    <div className="modal-ykf9">
                        <CheckIcon className="check-icon-ykf9" />
                        <span className="submit-success-message-ykf9">{labels.submitSuccessMessage}</span>
                    </div>
                </div>
            )}
            {submitStatus === "error" && (
                <div className="overlay-ykf9">
                    <div className="modal-ykf9">
                        <ErrorIcon className="error-icon-ykf9" />
                        <span className="submit-success-message-ykf9">Error</span>
                    </div>
                </div>
            )}
        </form>
    )
}

export default ContactUsFullForm
